import React, { useEffect, useState } from 'react'
import TreatmentStatsCards from './TreatmentStatsCards'
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { LocalHospital, LocationOn } from '@mui/icons-material'
import API from '../../../../../services/API'

const emptyStats = {
  totalPatients: 0,
  inTreatment: 0,
  waiting: 0,
  welfare: 0,
  availableBeds: 0,
  occupiedBeds: 0,
}

const BedManager = () => {
  const [summary, setSummary] = useState({
    ...emptyStats,
    locations: [],
  })
  const [loading, setLoading] = useState(true)

  const loadSummary = async () => {
    setLoading(true)

    try {
      const res = await API.get('/commandcontrol/bed-management/summary')

      if (res.status === 200) {
        setSummary({
          ...emptyStats,
          ...res.data,
          locations: res.data?.locations || [],
        })
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSummary()
  }, [])

  return (
    <Box>
      <Stack spacing={2.5}>
        <TreatmentStatsCards stats={summary} />

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 4,
            border: '1px solid rgba(15,23,42,0.08)',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
            >
              <Box>
                <Typography variant="h6" fontWeight={900}>
                  Treatment Locations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Live capacity across active control rooms.
                </Typography>
              </Box>

              <Chip
                label={`${summary.totalBeds ?? 0} configured beds`}
                sx={{ borderRadius: 999, fontWeight: 800 }}
              />
            </Stack>

            {loading ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading bed board...
                </Typography>
              </Stack>
            ) : summary.locations.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px dashed rgba(15,23,42,0.18)',
                  backgroundColor: '#F8FAFC',
                  textAlign: 'center',
                }}
              >
                <Typography fontWeight={900}>
                  No treatment locations configured
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add treatment centres from an active control room to start
                  tracking beds.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {summary.locations.map((location) => (
                  <Grid item xs={12} md={6} lg={4} key={location._id}>
                    <Paper
                      elevation={0}
                      onClick={() =>
                        window.open(`/treatmentlocations/${location._id}`)
                      }
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: '1px solid rgba(15,23,42,0.08)',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#F8FAFC' },
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#E6F7F9',
                              color: '#1E7F88',
                            }}
                          >
                            <LocalHospital />
                          </Box>

                          <Box minWidth={0}>
                            <Typography fontWeight={900} noWrap>
                              {location.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {location.type || 'Treatment Location'}
                            </Typography>
                          </Box>
                        </Stack>

                        {location.location && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <LocationOn fontSize="small" color="action" />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                            >
                              {location.location}
                            </Typography>
                          </Stack>
                        )}

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            label={`${location.stats?.totalPatients ?? 0} patients`}
                            sx={{ borderRadius: 999, fontWeight: 800 }}
                          />
                          <Chip
                            size="small"
                            label={`${location.stats?.availableBeds ?? 0} available`}
                            color="success"
                            sx={{ borderRadius: 999, fontWeight: 800 }}
                          />
                          <Chip
                            size="small"
                            label={`${location.stats?.occupiedBeds ?? 0}/${location.stats?.totalBeds ?? 0} occupied`}
                            color="error"
                            sx={{ borderRadius: 999, fontWeight: 800 }}
                          />
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  )
}

export default BedManager
